import { computed, watch, unref } from 'vue';
import { useQuery, keepPreviousData, useInfiniteQuery, useQueryClient, useMutation } from '@tanstack/vue-query';

// src/api/api-context.ts
var currentContext = null;
function setApiContext(context) {
  currentContext = context;
}
function getApiContext() {
  if (!currentContext) {
    throw new Error(
      "[@donoor/data-layer] ApiContext non configur\xE9. Appeler setApiContext() au boot de l'app."
    );
  }
  return currentContext;
}
function resetApiContext() {
  currentContext = null;
}

// src/api/api-error.ts
var ApiError = class extends Error {
  status;
  code;
  details;
  /** Présent si l'erreur vient d'un fetch JSON parsable. */
  rawResponse;
  constructor(opts) {
    super(opts.message);
    this.name = "ApiError";
    this.status = opts.status;
    this.code = opts.code;
    this.details = opts.details;
    this.rawResponse = opts.rawResponse;
  }
  /** True si l'erreur est due à un problème réseau (pas de réponse HTTP). */
  get isNetworkError() {
    return this.status === 0;
  }
  /** True si l'utilisateur n'est pas authentifié (401). */
  get isUnauthorized() {
    return this.status === 401;
  }
  /** True si l'utilisateur n'a pas la permission (403). */
  get isForbidden() {
    return this.status === 403;
  }
  /** True si la ressource n'existe pas (404). */
  get isNotFound() {
    return this.status === 404;
  }
  /** True si validation échouée (400 ou 422). */
  get isValidationError() {
    return this.status === 400 || this.status === 422;
  }
  /** True si erreur serveur (5xx). */
  get isServerError() {
    return this.status >= 500;
  }
};
async function apiErrorFromResponse(response) {
  let body;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (body && typeof body === "object" && "success" in body && body.success === false && "error" in body) {
    const errBody = body;
    const RESERVED = /* @__PURE__ */ new Set([
      "code",
      "message",
      "statusCode",
      "stack",
      "details"
    ]);
    const flatDetails = {};
    for (const [k, v] of Object.entries(errBody.error)) {
      if (!RESERVED.has(k)) flatDetails[k] = v;
    }
    const mergedDetails = errBody.error.details || Object.keys(flatDetails).length > 0 ? { ...flatDetails, ...errBody.error.details ?? {} } : void 0;
    return new ApiError({
      status: response.status,
      code: errBody.error.code ?? "HTTP_ERROR",
      message: errBody.error.message,
      details: mergedDetails,
      rawResponse: body
    });
  }
  if (body && typeof body === "object" && "statusCode" in body) {
    const h3 = body;
    const message = h3.data?.message || h3.message || h3.statusMessage;
    if (message) {
      return new ApiError({
        status: response.status,
        code: h3.data?.code ?? "HTTP_ERROR",
        message,
        details: h3.data,
        rawResponse: body
      });
    }
  }
  return new ApiError({
    status: response.status,
    code: response.status >= 500 ? "SERVER_ERROR" : "HTTP_ERROR",
    message: response.statusText || `HTTP ${response.status}`,
    rawResponse: body
  });
}
function apiErrorFromNetwork(err) {
  const message = err instanceof Error ? err.message : "Erreur r\xE9seau inconnue";
  return new ApiError({
    status: 0,
    code: "NETWORK_ERROR",
    message
  });
}

// src/api/api-client.ts
async function apiCall(path, options = {}) {
  const ctx = getApiContext();
  const {
    method = "GET",
    body,
    query,
    headers = {},
    skipOrganizationId = false,
    skipAuth = false,
    skipUnauthorizedHandler = false,
    signal,
    timeoutMs = 2e4
  } = options;
  let timeoutId;
  let effectiveSignal = signal;
  if (timeoutMs > 0) {
    const timeoutController = new AbortController();
    timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
    const anyFn = AbortSignal.any;
    effectiveSignal = signal && typeof anyFn === "function" ? anyFn([signal, timeoutController.signal]) : signal ?? timeoutController.signal;
  }
  const url = new URL(path, ctx.apiUrl);
  if (!skipOrganizationId) {
    const orgId = ctx.getOrganizationId();
    if (orgId && !url.searchParams.has("organizationId")) {
      url.searchParams.set("organizationId", orgId);
    }
  }
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== null && value !== void 0) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  const extra = ctx.getExtraQuery?.(method, url.pathname);
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      if (!url.searchParams.has(key)) url.searchParams.set(key, value);
    }
  }
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const finalHeaders = { ...headers };
  if (body !== void 0 && !isFormData && !finalHeaders["Content-Type"]) {
    finalHeaders["Content-Type"] = "application/json";
  }
  if (!skipAuth) {
    const token = await ctx.getAuthToken();
    if (token) {
      finalHeaders["Authorization"] = `Bearer ${token}`;
    }
  }
  if (ctx.getExtraHeaders) {
    const extra2 = ctx.getExtraHeaders();
    if (extra2) {
      for (const [key, value] of Object.entries(extra2)) {
        if (value !== void 0 && value !== null && value !== "") {
          finalHeaders[key] = value;
        }
      }
    }
  }
  if (/(\.ngrok-free\.(app|dev)|\.ngrok\.io|\.trycloudflare\.com)$/.test(url.hostname)) {
    finalHeaders["ngrok-skip-browser-warning"] = "true";
  }
  const fetchFn = ctx.fetch ?? globalThis.fetch;
  const doFetch = async () => {
    try {
      const serializedBody = body === void 0 ? void 0 : isFormData ? body : JSON.stringify(body);
      return await fetchFn(url.toString(), {
        method,
        headers: finalHeaders,
        body: serializedBody,
        signal: effectiveSignal
      });
    } catch (err) {
      throw apiErrorFromNetwork(err);
    }
  };
  let response = await doFetch();
  if (response.status === 401 && ctx.onUnauthorized && !skipAuth && !skipUnauthorizedHandler) {
    await ctx.onUnauthorized();
    const refreshedToken = await ctx.getAuthToken();
    if (refreshedToken && refreshedToken !== finalHeaders["Authorization"]?.replace("Bearer ", "")) {
      finalHeaders["Authorization"] = `Bearer ${refreshedToken}`;
      response = await doFetch();
    }
  }
  if (timeoutId !== void 0) clearTimeout(timeoutId);
  if (!response.ok) {
    throw await apiErrorFromResponse(response);
  }
  let parsed;
  try {
    parsed = await response.json();
  } catch (err) {
    throw new ApiError({
      status: response.status,
      code: "INVALID_JSON",
      message: "R\xE9ponse API non parsable en JSON"
    });
  }
  return parsed;
}
async function apiCallData(path, options = {}) {
  const result = await apiCall(path, options);
  if (!result || typeof result !== "object" || !("data" in result)) {
    throw new ApiError({
      status: 500,
      code: "INVALID_RESPONSE_SHAPE",
      message: "R\xE9ponse API ne contient pas de champ `data`",
      rawResponse: result
    });
  }
  return result.data;
}

// src/factory/cache-strategies.ts
function reessaiSiUtile(fois) {
  return (echecs, erreur) => {
    if (echecs >= fois) return false;
    const statut = erreur?.status;
    if (typeof statut !== "number") return true;
    return statut === 0 || statut === 408 || statut === 429 || statut >= 500;
  };
}
var SECOND = 1e3;
var MINUTE = 60 * SECOND;
var HOUR = 60 * MINUTE;
var REALTIME = {
  staleTime: 30 * SECOND,
  gcTime: 5 * MINUTE,
  retry: reessaiSiUtile(2),
  refetchOnWindowFocus: true,
  refetchOnReconnect: true
};
var FREQUENT = {
  staleTime: 2 * MINUTE,
  gcTime: 10 * MINUTE,
  retry: reessaiSiUtile(2),
  refetchOnWindowFocus: true,
  refetchOnReconnect: true
};
var STANDARD = {
  staleTime: 5 * MINUTE,
  gcTime: 30 * MINUTE,
  retry: reessaiSiUtile(2),
  refetchOnWindowFocus: false,
  refetchOnReconnect: true
};
var STABLE = {
  staleTime: 30 * MINUTE,
  gcTime: HOUR,
  retry: reessaiSiUtile(1),
  refetchOnWindowFocus: false,
  refetchOnReconnect: false
};
var IMMUTABLE = {
  staleTime: Infinity,
  gcTime: Infinity,
  retry: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false
};
var CACHE_STRATEGIES = {
  REALTIME,
  FREQUENT,
  STANDARD,
  STABLE,
  IMMUTABLE
};
function resolveParams(params) {
  const out = {};
  for (const k in params) {
    out[k] = unref(params[k]);
  }
  return out;
}
function defineOrgQuery(config) {
  const {
    key,
    fetch: fetchFn,
    cache = STANDARD,
    allowedWithoutOrg = false,
    enableWhen,
    invalidationTags = [],
    keepPreviousData: keepPrev = false,
    refetchInterval
  } = config;
  const parametresParCle = /* @__PURE__ */ new Map();
  return function useDefinedQuery(...args) {
    const params = args[0] ?? {};
    const ctx = getApiContext();
    const orgId = computed(() => ctx.getOrganizationId());
    const resolved = computed(() => resolveParams(params));
    const queryKey = computed(() => {
      const baseKey = key(resolved.value);
      const cle = [...baseKey, orgId.value ?? "no-org"];
      parametresParCle.set(JSON.stringify(cle), resolved.value);
      return cle;
    });
    const enabled = computed(() => {
      const orgOk = allowedWithoutOrg || !!orgId.value;
      if (!orgOk) return false;
      if (enableWhen && !enableWhen(resolved.value)) return false;
      return true;
    });
    const query = useQuery({
      queryKey,
      queryFn: async ({ signal, queryKey: cleChargee }) => {
        const currentOrgId = orgId.value;
        if (!currentOrgId && !allowedWithoutOrg) {
          throw new Error("OrganizationId requis mais absent");
        }
        const p = parametresParCle.get(JSON.stringify(cleChargee)) ?? resolved.value;
        return fetchFn(currentOrgId ?? "", p, { signal });
      },
      enabled,
      staleTime: cache.staleTime,
      gcTime: cache.gcTime,
      retry: cache.retry,
      refetchOnWindowFocus: cache.refetchOnWindowFocus,
      refetchOnReconnect: cache.refetchOnReconnect,
      // Rafraîchissement périodique, uniquement si la query le demande.
      ...refetchInterval ? { refetchInterval } : {},
      // Garde l'ancienne data affichée pendant qu'une nouvelle clé charge
      // (changement de filtre/période) — opt-in via `keepPreviousData`.
      ...keepPrev ? { placeholderData: keepPreviousData } : {},
      // ── Temps réel : expose les `invalidationTags` en `meta.cache_tags` ──
      // C'est ce que le listener WebSocket `cache:invalidate` lit pour décider
      // quelles queries rafraîchir. Le backend émet des tags SUFFIXÉS par org
      // (`transactions:343`), donc on génère pour chaque tag les DEUX formes :
      // `transactions` (générique) ET `transactions:{orgId}` (ciblée org).
      // Fonction (réévaluée à chaque invalidation) pour suivre l'orgId courant.
      // Sans ça, `invalidationTags` était silencieusement ignoré → les listes
      // (ex: page Dons) ne se mettaient JAMAIS à jour en temps réel.
      meta: invalidationTags.length > 0 ? {
        cache_tags: () => {
          const id = orgId.value;
          return invalidationTags.flatMap(
            (t) => id != null ? [t, `${t}:${id}`] : [t]
          );
        }
      } : void 0
    });
    watch(
      orgId,
      (id) => {
        if (id && enabled.value && query.isPending.value && query.data.value === void 0) {
          query.refetch();
        }
      },
      { immediate: true }
    );
    return query;
  };
}
function defineOrgInfiniteQuery(config) {
  const {
    key,
    fetch: fetchFn,
    cache = STANDARD,
    allowedWithoutOrg = false,
    enableWhen,
    invalidationTags = [],
    maxPages,
    initialPageParam: initialPageParamFn,
    getPreviousPageParam: getPreviousPageParamFn,
    keepPreviousData: keepPrev = false
  } = config;
  return function useDefinedInfiniteQuery(...args) {
    const params = args[0] ?? {};
    const ctx = getApiContext();
    const orgId = computed(() => ctx.getOrganizationId());
    const resolved = computed(() => resolveParams(params));
    const initialOffset = computed(
      () => initialPageParamFn ? initialPageParamFn(resolved.value) : 0
    );
    const queryKey = computed(() => {
      const baseKey = key(resolved.value);
      const anchor = initialPageParamFn ? [`anchor:${initialOffset.value}`] : [];
      return [...baseKey, orgId.value ?? "no-org", "infinite", ...anchor];
    });
    const enabled = computed(() => {
      const orgOk = allowedWithoutOrg || !!orgId.value;
      if (!orgOk) return false;
      if (enableWhen && !enableWhen(resolved.value)) return false;
      return true;
    });
    const stableQueryFn = async ({
      pageParam,
      signal
    }) => {
      const currentOrgId = orgId.value;
      if (!currentOrgId && !allowedWithoutOrg) {
        throw new Error("OrganizationId requis mais absent");
      }
      return fetchFn(currentOrgId ?? "", resolved.value, pageParam, { signal });
    };
    const stableGetNextPageParam = (lastPage) => lastPage.meta.hasMore ? lastPage.meta.offset + lastPage.meta.limit : void 0;
    const query = useInfiniteQuery(
      computed(() => ({
        queryKey: queryKey.value,
        initialPageParam: initialOffset.value,
        queryFn: stableQueryFn,
        getNextPageParam: stableGetNextPageParam,
        // Scroll bidirectionnel (remonter au-dessus de l'ancrage).
        ...getPreviousPageParamFn ? { getPreviousPageParam: getPreviousPageParamFn } : {},
        // Fenêtre glissante de pages (perf DOM) si configuré.
        ...maxPages ? { maxPages } : {},
        enabled: enabled.value,
        staleTime: cache.staleTime,
        gcTime: cache.gcTime,
        retry: cache.retry,
        refetchOnWindowFocus: cache.refetchOnWindowFocus,
        refetchOnReconnect: cache.refetchOnReconnect,
        ...keepPrev ? { placeholderData: keepPreviousData } : {},
        meta: invalidationTags.length > 0 ? {
          cache_tags: () => {
            const id = orgId.value;
            return invalidationTags.flatMap(
              (t) => id != null ? [t, `${t}:${id}`] : [t]
            );
          }
        } : void 0
      }))
    );
    watch(
      orgId,
      (id) => {
        if (id && enabled.value && query.isPending.value && query.data.value === void 0) {
          query.refetch();
        }
      },
      { immediate: true }
    );
    return query;
  };
}
function defineOrgMutation(config) {
  const {
    mutate,
    invalidationTags = [],
    allowedWithoutOrg = false,
    optimisticUpdate
  } = config;
  return function useDefinedMutation() {
    const ctx = getApiContext();
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (variables) => {
        const orgId = ctx.getOrganizationId();
        if (!orgId && !allowedWithoutOrg) {
          throw new Error("OrganizationId requis pour cette mutation");
        }
        return mutate(orgId ?? "", variables);
      },
      // Optimistic : applique le changement local AVANT la réponse serveur.
      // On annule les refetch en vol sur les tags (sinon ils écraseraient
      // l'optimiste) et on mémorise le rollback. Le contexte renvoyé est passé
      // à onError pour restaurer en cas d'échec. Karim 2026-06-30.
      onMutate: optimisticUpdate ? async (variables) => {
        for (const tag of invalidationTags) {
          await queryClient.cancelQueries({ queryKey: [tag] });
        }
        const rollback = optimisticUpdate(queryClient, variables);
        return { rollback };
      } : void 0,
      onError: optimisticUpdate ? (_err, _vars, context) => {
        const rb = context?.rollback;
        if (typeof rb === "function") rb();
      } : void 0,
      /**
       * ⚠️ `await` LOAD-BEARING (Karim 2026-09-22).
       *
       * TanStack attend la promesse renvoyée par `onSuccess` avant de résoudre
       * `mutateAsync`. Sans ce `await`, l'invalidation partait en arrière-plan
       * et la mutation se résolvait AVANT que la liste ne soit rechargée : le
       * voile + spinner de la carte (piloté par le `finally` de l'appelant)
       * disparaissait ~1 s avant que la carte supprimée ne s'en aille
       * réellement. L'asso voyait une carte « supprimée » encore cliquable.
       *
       * `Promise.all` plutôt qu'une boucle séquentielle : les tags sont
       * indépendants, autant recharger en parallèle.
       */
      onSuccess: async () => {
        await Promise.all(
          invalidationTags.map(
            (tag) => queryClient.invalidateQueries({ queryKey: [tag] })
          )
        );
      }
    });
  };
}

// src/ws-invalidation/ws-invalidation.ts
function bindWsInvalidation(socket, queryClient, options = {}) {
  const { eventName = "cache:invalidate", debug = false } = options;
  const handler = (payload) => {
    if (!isValidPayload(payload)) {
      if (debug) {
        console.warn("[ws-invalidation] Payload invalide:", payload);
      }
      return;
    }
    for (const tag of payload.tags) {
      if (debug) {
        console.log(
          `[ws-invalidation] Invalidation tag "${tag}"`,
          payload.organizationId ? `(org ${payload.organizationId})` : "(global)"
        );
      }
      queryClient.invalidateQueries({ queryKey: [tag] });
    }
  };
  socket.on(eventName, handler);
  return function unbind() {
    socket.off(eventName, handler);
  };
}
function isValidPayload(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (!("tags" in payload)) return false;
  const tags = payload.tags;
  return Array.isArray(tags) && tags.every((t) => typeof t === "string");
}
function invalidateByTags(queryClient, tags) {
  for (const tag of tags) {
    queryClient.invalidateQueries({ queryKey: [tag] });
  }
}
function useQueryBoundary(query, options = {}) {
  const isEmpty = options.isEmpty ?? ((data) => data === void 0 || data === null);
  const showError = computed(
    () => !!query.error.value && query.data.value === void 0
  );
  const showSkeleton = computed(
    () => query.isPending.value && query.data.value === void 0 && !query.error.value
  );
  const showEmpty = computed(
    () => !showSkeleton.value && !showError.value && isEmpty(query.data.value)
  );
  const showData = computed(
    () => !showSkeleton.value && !showError.value && !showEmpty.value
  );
  const isRefetching = computed(
    () => query.isFetching.value && query.data.value !== void 0
  );
  return {
    showSkeleton,
    showError,
    showEmpty,
    showData,
    isRefetching
  };
}

export { ApiError, CACHE_STRATEGIES, FREQUENT, IMMUTABLE, REALTIME, STABLE, STANDARD, apiCall, apiCallData, apiErrorFromNetwork, apiErrorFromResponse, bindWsInvalidation, defineOrgInfiniteQuery, defineOrgMutation, defineOrgQuery, getApiContext, invalidateByTags, reessaiSiUtile, resetApiContext, setApiContext, useQueryBoundary };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map